import { useEffect, useState } from "react";

const taglines = [
  "Pro Gamer. Content Creator. Unstoppable.",
  "Ranked. Skilled. Legendary.",
  "Live. Stream. Dominate.",
];

export default function Hero() {
  const [taglineIndex, setTaglineIndex] = useState(0);
  const [displayed, setDisplayed] = useState("");
  const [charIndex, setCharIndex] = useState(0);

  useEffect(() => {
    const current = taglines[taglineIndex];
    if (charIndex < current.length) {
      const timeout = setTimeout(() => {
        setDisplayed(current.slice(0, charIndex + 1));
        setCharIndex((c) => c + 1);
      }, 55);
      return () => clearTimeout(timeout);
    } else {
      const pause = setTimeout(() => {
        setCharIndex(0);
        setDisplayed("");
        setTaglineIndex((i) => (i + 1) % taglines.length);
      }, 2800);
      return () => clearTimeout(pause);
    }
  }, [charIndex, taglineIndex]);

  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative w-full min-h-screen flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src="https://static.readdy.ai/image/c088ed944a840dff525f6c9a83fbd586/5333f327a4d27a8826ca98f172e76bdf.png"
          alt="Aidan Barrett Hero"
          className="w-full h-full object-cover object-center"
        />
        {/* Layered dark overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#07070f]/70 via-[#07070f]/30 to-[#07070f]/90" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#07070f]/60 via-transparent to-[#07070f]/60" />
        {/* Scanline overlay */}
        <div className="absolute inset-0 scanline-overlay opacity-30" />
      </div>

      {/* Neon corner accents */}
      <div className="absolute top-20 left-8 w-16 h-16 border-l-2 border-t-2 border-[#e879f9]/50" />
      <div className="absolute top-20 right-8 w-16 h-16 border-r-2 border-t-2 border-[#22d3ee]/50" />
      <div className="absolute bottom-16 left-8 w-16 h-16 border-l-2 border-b-2 border-[#22d3ee]/50" />
      <div className="absolute bottom-16 right-8 w-16 h-16 border-r-2 border-b-2 border-[#e879f9]/50" />

      {/* Content */}
      <div className="relative z-10 w-full flex flex-col items-center text-center px-6 animate-fade-in">
        {/* Status badge */}
        <div className="flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full border border-[#e879f9]/30 bg-[#e879f9]/10 backdrop-blur-sm">
          <span className="w-2 h-2 rounded-full bg-[#e879f9] animate-pulse inline-block" />
          <span className="font-mono-tech text-xs text-[#e879f9] uppercase tracking-widest">
            Online &amp; Streaming
          </span>
        </div>

        {/* Name */}
        <h1 className="font-orbitron text-5xl md:text-7xl lg:text-8xl font-black uppercase tracking-tight mb-3">
          <span className="text-white">AIDAN </span>
          <span className="neon-text-fuchsia animate-glow-pulse">BARRETT</span>
        </h1>

        {/* Typing tagline */}
        <div className="h-8 flex items-center justify-center mb-8">
          <p className="font-mono-tech text-lg md:text-xl neon-text-cyan">
            {displayed}
            <span className="animate-pulse">|</span>
          </p>
        </div>

        {/* Stats row */}
        <div className="flex items-center gap-8 mb-10 flex-wrap justify-center">
          {[
            { val: "450K+", label: "Followers" },
            { val: "4,200+", label: "Hours Live" },
            { val: "300+", label: "Games Beaten" },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-orbitron text-2xl font-bold text-white">{s.val}</div>
              <div className="font-rajdhani text-xs uppercase tracking-widest text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="flex items-center gap-4 flex-wrap justify-center">
          <button
            onClick={() => scrollTo("#streams")}
            className="btn-neon font-orbitron text-sm font-bold uppercase tracking-widest px-8 py-3 rounded-md bg-[#e879f9]/10 border-2 border-[#e879f9] text-[#e879f9] hover:bg-[#e879f9]/20 animate-border-glow whitespace-nowrap cursor-pointer transition-all duration-300"
          >
            <i className="ri-live-line mr-2" />
            Watch Live
          </button>
          <button
            onClick={() => scrollTo("#highlights")}
            className="btn-neon font-orbitron text-sm font-bold uppercase tracking-widest px-8 py-3 rounded-md bg-transparent border-2 border-[#22d3ee]/60 text-[#22d3ee] hover:bg-[#22d3ee]/10 whitespace-nowrap cursor-pointer transition-all duration-300"
          >
            <i className="ri-play-circle-line mr-2" />
            View Highlights
          </button>
        </div>

        {/* Social quick links */}
        <div className="flex items-center gap-5 mt-10">
          {[
            { icon: "ri-twitch-fill", color: "#9147ff" },
            { icon: "ri-youtube-fill", color: "#ff0000" },
            { icon: "ri-twitter-x-fill", color: "#e2e8f0" },
            { icon: "ri-discord-fill", color: "#5865f2" },
            { icon: "ri-tiktok-fill", color: "#ff0050" },
          ].map((s, i) => (
            <a
              key={i}
              href="#socials"
              onClick={(e) => { e.preventDefault(); scrollTo("#socials"); }}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-white/10 bg-white/5 backdrop-blur-sm hover:border-white/30 hover:bg-white/10 transition-all duration-300 cursor-pointer"
              style={{ color: s.color }}
            >
              <i className={`${s.icon} text-lg`} />
            </a>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-float">
        <span className="font-mono-tech text-xs text-slate-500 uppercase tracking-widest">Scroll</span>
        <i className="ri-arrow-down-line text-[#e879f9] text-xl" />
      </div>
    </section>
  );
}
